<template>
  <section class="emotional-stability">
    <div class="emotional-stability__header">
      <h2 class="emotional-stability__title">
        Эмоциональная устойчивость и мотивация
      </h2>

      <div class="emotional-stability__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            class="emotional-stability__select"
            :disabled="!hasSubscription"
        />

        <div>
          <a-range-picker
              v-model:value="selectedPeriod"
              :placeholder="['Дата начала', 'Дата конца']"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              separator="-"
              inputReadOnly
              class="emotional-stability__date compact-range-picker"
              :disabled="!hasSubscription"
          />
        </div>
      </div>
    </div>

    <div class="emotional-stability__grid">
      <SmallCardWrapper
          name="Индекс хронической усталости"
          :helpText="'В виджете показаны средний индекс хронической усталости, сколько сотрудников в процентах имеет тот или иной уровень хронической усталости по выбранной группе за данный период. Стрелка рядом показывает рост/снижение процента сотрудников на разных уровнях по сравнению с предыдущим периодом. А также показаны средние индексы хронической усталости по пяти уровням должностей.<br><br>Четыре уровня:<br><br>Низкий уровень - от 0 до 2 баллов<br>Умеренный уровень - свыше 2 и до 5 баллов<br>Высокий уровень - свыше 5 и до 8 баллов<br>Критический уровень - свыше 8 и до 10 баллов'"
          :subtitleLines="fatigueHeaderLines"
          :headerMetric="fatigueCurrentValue !== null && fatigueCurrentValue !== undefined ? `${fatigueCurrentValue}` : ''"
          :loading="dataIsLoading"
          :showLockedContent="true"
          :locked="!hasSubscription"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Отслеживайте индекс хронической усталости.'" />
        </div>
        <ChronicFatigueIndexCard
            :levels="fatigueLevels"
            :positions="fatiguePositions"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Детализация индекса хронической усталости"
          :helpText="'Индекс хронической усталости включает в себя отдельные показатели по стрессу, монотонии и снижению мотивации, результаты которых соотносятся с четырьмя уровнями:<br>Низкий уровень - от 0 до 2 баллов<br>Умеренный уровень - свыше 2 и до 5 баллов<br>Высокий уровень - свыше 5 и до 8 баллов<br>Критический уровень - свыше 8 и до 10 баллов'"
          :subtitle="stabilityPassedCount ? formatPassedCount(stabilityPassedCount) : ''"
          :loading="dataIsLoading"
          :showLockedContent="true"
          :locked="!hasSubscription"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Отслеживайте индекс устойчивости и мотивации.'" />
        </div>
        <StabilityMotivationIndexCard
            :items="stabilityItems"
            :chartLabels="stabilityChart.labels"
            :chartDatasets="stabilityChart.datasets"
            :subindexLevelCounts="stabilitySubindexLevelCounts"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'

import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import StabilityMotivationIndexCard from '@/components/Analytics/StabilityMotivationIndexCard.vue'
import ChronicFatigueIndexCard from '@/components/Analytics/ChronicFatigueIndexCard.vue'
import SubscriptionRequiredWarning from '@/components/OtherElements/SubscriptionRequiredWarning.vue'
import SoftSkillsGrowthCard from "@/components/Analytics/SoftSkillsGrowthCard.vue";
import { formatMonitoringDateForPicker, getMonitoringDateSetting, setMonitoringDateSetting } from "@/utils/monitoringDateStorage"
import { getWordFormBasedOnTheCount } from '@/views/Report/utils'

export default {
  name: 'EmotionalStability',

  props: {
    hasSubscription: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SoftSkillsGrowthCard,
    SubscriptionRequiredWarning,
    ChronicFatigueIndexCard,
    StabilityMotivationIndexCard,
    SmallCardWrapper,
  },

  setup(props) {
    const store = useStore();
    const groups = computed(() => store.state.group.groups);
    const dateStorageKey = 'monitoring:emotional-stability:date'
    const defaultSelectedPeriod = [
      dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
      dayjs().format('YYYY-MM-DD'),
    ]
    const savedSelectedPeriod = getMonitoringDateSetting(dateStorageKey, defaultSelectedPeriod)
    const pickerSelectedPeriod = Array.isArray(savedSelectedPeriod)
        ? savedSelectedPeriod.map(formatMonitoringDateForPicker)
        : savedSelectedPeriod
    const savedSelectedPeriodIsValid = Array.isArray(pickerSelectedPeriod) && pickerSelectedPeriod.length === 2
    const dateRangesAreEqual = (firstRange, secondRange) => {
      return Array.isArray(firstRange) &&
          Array.isArray(secondRange) &&
          firstRange.length === 2 &&
          secondRange.length === 2 &&
          firstRange[0] === secondRange[0] &&
          firstRange[1] === secondRange[1]
    }
    const hasSavedCustomDate = savedSelectedPeriodIsValid && !dateRangesAreEqual(pickerSelectedPeriod, defaultSelectedPeriod)

    const selectedGroup = ref('all');
    const selectedPeriod = ref(savedSelectedPeriodIsValid ? pickerSelectedPeriod : defaultSelectedPeriod);
    const dataIsLoading = ref(false);

    const hasSubscriptionAccess = computed(() => props.hasSubscription);

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    })

    const emotionalStabilityDynamicsData = computed(() => {
      return store.state.analytics.monitoringData?.emotionalStabilityDynamics ?? null
    });

    const DEFAULT_STABILITY_ITEMS = [
      {
        key: 'resilience',
        label: 'Хроническая усталость',
        change: 6.1,
        value: 8.2,
        color: '#5C7CFA',
      },
      {
        key: 'stress',
        label: 'Индекс стресса',
        change: 0,
        value: 2,
        color: '#46C7D8',
      },
      {
        key: 'monotony',
        label: 'Индекс монотонии',
        change: -4,
        value: -1,
        color: '#E333C8',
      },
      {
        key: 'motivation',
        label: 'Снижение мотивации',
        change: 3.4,
        value: 5,
        color: '#B45CFF',
        warning: 'Снижененная мотивация'
      },
    ]

    const DEFAULT_STABILITY_CHART = {
      labels: ['II кв.', 'III кв.', 'IV кв.', 'I кв.'],
      datasets: [
        {
          key: 'resilience',
          label: 'Хроническая усталость',
          values: [-2, 3, -1, -0.5],
          color: '#5C7CFA',
        },
        {
          key: 'stress',
          label: 'Индекс стресса',
          values: [1, 9.5, 0.5, 3],
          color: '#46C7D8',
        },
        {
          key: 'monotony',
          label: 'Индекс монотонии',
          values: [-9, 2, -5.5, -3],
          color: '#E333C8',
        },
        {
          key: 'motivation',
          label: 'Снижение мотивации',
          values: [7, 6, 9, 7],
          color: '#B45CFF',
        },
      ],
    }

    const DEFAULT_FATIGUE_LEVELS = [
      {
        key: 'low',
        label: 'Низкий уровень',
        change: 7,
        value: 6,
        color: '#21AC0C',
      },
      {
        key: 'moderate',
        label: 'Умеренный',
        change: 10,
        value: 24,
        color: '#99DA6E',
      },
      {
        key: 'high',
        label: 'Высокий',
        change: 15,
        value: 42,
        color: '#FF9F40',
      },
      {
        key: 'critical',
        label: 'Критический',
        change: 3,
        value: 12,
        color: '#F55565',
      },
    ]

    const DEFAULT_FATIGUE_POSITIONS = [
      {
        name: 'Линейный персонал',
        value: 3,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Специалисты',
        value: 8,
        max: 10,
        color: '#FF9F40',
      },
      {
        name: 'Старшие специалисты',
        value: 5,
        max: 10,
        color: '#99DA6E',
      },
      {
        name: 'Руководители',
        value: 1,
        max: 10,
        color: '#21AC0C',
      },
      {
        name: 'Топ-менеджмент',
        value: 10,
        max: 10,
        color: '#F55565',
      },
    ]

    const stabilityItems = ref([])
    const stabilityChart = ref({
      labels: [],
      datasets: [],
    })
    const fatigueLevels = ref([])
    const fatiguePositions = ref([])
    const stabilitySubindexLevelCounts = ref({})
    const stabilityPassedCount = ref(null)
    const fatiguePassedCount = ref(null)
    const fatigueCurrentValue = ref(null)

    const getSafeNumber = (value) => {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : 0
    }

    const formatPassedCount = (count) => {
      return `(${count} ${getWordFormBasedOnTheCount(count, 'человек', 'человека', 'человек')})`
    }

    const fatigueHeaderLines = computed(() => {
      return [
        fatiguePassedCount.value ? formatPassedCount(fatiguePassedCount.value) : '',
      ].filter(Boolean)
    })

    const getSignedChange = (score, previous, trend) => {
      const diff = Math.abs(getSafeNumber(score) - getSafeNumber(previous))

      if (trend === 'down') {
        return -diff
      }

      if (trend === 'up') {
        return diff
      }

      return getSafeNumber(score) - getSafeNumber(previous)
    }

    const getSignedDirectionChange = (value, direction) => {
      const safeValue = Math.abs(getSafeNumber(value))

      if (direction === 'down') {
        return -safeValue
      }

      if (direction === 'up') {
        return safeValue
      }

      return 0
    }

    const normalizeBackendData = (data) => {
      return data?.emotional_stability_and_motivation || data || {}
    }

    const getColorByName = (color) => {
      const normalizedColor = String(color || '').toLowerCase()

      if (normalizedColor === 'green') {
        return '#21AC0C'
      }

      if (normalizedColor === 'red') {
        return '#F55565'
      }

      if (normalizedColor === 'neutral') {
        return '#D9D9D9'
      }

      return color || '#D9D9D9'
    }

    const getStabilityItemFallback = (key) => {
      return DEFAULT_STABILITY_ITEMS.find((item) => item.key === key) || {}
    }

    const getStabilityDatasetFallback = (key) => {
      return DEFAULT_STABILITY_CHART.datasets.find((item) => item.key === key) || {}
    }

    const mapMetricToItem = (key, metric, color) => {
      const fallback = getStabilityItemFallback(key)

      return {
        key,
        label: metric?.label || fallback.label || '',
        change: metric?.score_difference !== undefined
            ? getSignedDirectionChange(metric?.score_difference, metric?.direction)
            : getSignedChange(metric?.score, metric?.previous, metric?.trend),
        value: getSafeNumber(metric?.current_value ?? metric?.score),
        color: color || fallback.color,
        warning: metric?.warning || metric?.description || '',
      }
    }

    const mapChartDataset = (key, label, values, color) => {
      const fallback = getStabilityDatasetFallback(key)

      return {
        key,
        label: label || fallback.label || '',
        values: Array.isArray(values)
            ? values.map((value) => getSafeNumber(value))
            : [],
        color: color || fallback.color,
      }
    }

    const getFatigueLevelColor = (title) => {
      const normalizedTitle = String(title).toLowerCase()

      if (normalizedTitle.includes('низ')) {
        return '#21AC0C'
      }

      if (normalizedTitle.includes('умер')) {
        return '#99DA6E'
      }

      if (normalizedTitle.includes('высок')) {
        return '#FF9F40'
      }

      if (normalizedTitle.includes('крит')) {
        return '#F55565'
      }

      return '#D9D9D9'
    }

    const getFatigueCategoryColor = (type) => {
      const colors = {
        low: '#21AC0C',
        moderate: '#99DA6E',
        high: '#FF9F43',
        critical: '#F55565',
      }

      return colors[type] || '#D1D1D1'
    }

    const FATIGUE_CATEGORY_ORDER = ['low', 'moderate', 'high', 'critical']
    const FATIGUE_CATEGORY_LABELS = {
      low: 'Низкий уровень',
      moderate: 'Умеренный уровень',
      high: 'Высокий уровень',
      critical: 'Критический уровень',
    }

    const mapFatigueCategories = (categories) => {
      return FATIGUE_CATEGORY_ORDER.map((type) => {
        const item = categories.find((category) => category.type === type) || {
          type,
          label: FATIGUE_CATEGORY_LABELS[type],
          percent: 0,
          percent_difference: 0,
          direction: 'same',
          color: 'neutral',
        }
        const direction = item.direction || 'same'

        return {
          key: item.type,
          label: item.label,
          change: getSignedDirectionChange(item.percent_difference, direction),
          direction,
          value: getSafeNumber(item.percent),
          color: item.color === 'neutral'
              ? '#D1D1D1'
              : getFatigueCategoryColor(item.type),
          changeColor: item.color,
        }
      })
    }

    const STABILITY_LEVEL_KEYS = ['low', 'moderate', 'high', 'critical']

    const getLevelCountValue = (value) => {
      return getSafeNumber(value.count)
    }

    const mapStabilityLevelCounts = (source) => {
      return STABILITY_LEVEL_KEYS.map((key) => {
        const item = source.find((level) => level.type === key) || {
          type: key,
          count: 0,
        }

        return {
          key,
          value: getLevelCountValue(item),
        }
      })
    }

    const mapStabilitySubindexLevelCounts = (metrics) => {
      return {
        stress: mapStabilityLevelCounts(metrics.stress.categories),
        monotony: mapStabilityLevelCounts(metrics.monotony.categories),
        motivation: mapStabilityLevelCounts(metrics.motivation.categories),
      }
    }

    const periodInterval = computed(() => {
      if (
          !Array.isArray(selectedPeriod.value) ||
          selectedPeriod.value.length !== 2 ||
          !selectedPeriod.value[0] ||
          !selectedPeriod.value[1]
      ) {
        return null
      }

      const [from, to] = selectedPeriod.value

      return [
        dayjs(from).format('DD.MM.YYYY'),
        dayjs(to).format('DD.MM.YYYY'),
      ].join('-')
    })

    const buildStabilityFromBackend = (data) => {
      const backendData = normalizeBackendData(data)
      const nextResilience = backendData?.resilience_motivation_index || {}
      const chart = nextResilience?.chart || {}

      if (
          chart?.labels ||
          nextResilience?.chronic_fatigue_index ||
          nextResilience?.stress ||
          nextResilience?.monotony ||
          nextResilience?.motivation_decrease
      ) {
        const metricMap = [
          {
            key: 'resilience',
            metric: nextResilience?.chronic_fatigue_index,
            chartKey: 'chronic_fatigue_index',
            color: '#597EF7',
          },
          {
            key: 'stress',
            metric: nextResilience?.stress,
            chartKey: 'stress',
            color: '#2AC8E0',
          },
          {
            key: 'monotony',
            metric: nextResilience?.monotony,
            chartKey: 'monotony',
            color: '#F323C6',
          },
          {
            key: 'motivation',
            metric: nextResilience?.motivation_decrease,
            chartKey: 'motivation_decrease',
            color: '#AE3FF3',
          },
        ]

        return {
          passedCount: nextResilience.passed_count,
          subindexLevelCounts: mapStabilitySubindexLevelCounts({
            stress: nextResilience?.stress,
            monotony: nextResilience?.monotony,
            motivation: nextResilience?.motivation_decrease,
          }),
          items: metricMap
              .map((item) => mapMetricToItem(item.key, item.metric, item.color))
              .filter((item) => item.label || item.value || item.change),
          chart: {
            labels: Array.isArray(chart?.labels) ? [...chart.labels] : [],
            datasets: metricMap
                .map((item) => mapChartDataset(
                    item.key,
                    item.metric?.label,
                    chart?.[item.chartKey],
                    item.color
                ))
                .filter((item) => item.values.length),
          },
        }
      }

      const resilience = data?.resilience_motivation_index || {}

      const items = [
        {
          key: 'resilience',
          label: 'Хроническая усталость',
          change: getSignedChange(resilience?.score, resilience?.previous, resilience?.trend),
          value: getSafeNumber(resilience?.score),
          color: '#597EF7',
        },
        {
          key: 'stress',
          label: 'Индекс стресса',
          change: getSignedChange(
              resilience?.stress_index?.score,
              resilience?.stress_index?.previous,
              resilience?.stress_index?.trend
          ),
          value: getSafeNumber(resilience?.stress_index?.score),
          color: '#2AC8E0',
        },
        {
          key: 'monotony',
          label: 'Индекс монотонии',
          change: getSignedChange(
              resilience?.monotony_index?.score,
              resilience?.monotony_index?.previous,
              resilience?.monotony_index?.trend
          ),
          value: getSafeNumber(resilience?.monotony_index?.score),
          color: '#F323C6',
        },
        {
          key: 'motivation',
          label: 'Снижение мотивации',
          change: getSignedChange(
              resilience?.decreased_motivation?.score,
              resilience?.decreased_motivation?.previous,
              resilience?.decreased_motivation?.trend
          ),
          value: getSafeNumber(resilience?.decreased_motivation?.score),
          color: '#AE3FF3',
        },
      ]

      return {
        passedCount: resilience.passed_count,
        subindexLevelCounts: {
          stress: [],
          monotony: [],
          motivation: [],
        },
        items,
        chart: {
          labels: [],
          datasets: [
            {
              key: 'resilience',
              label: 'Хроническая усталость',
              values: [getSafeNumber(resilience?.score)],
              color: '#597EF7',
            },
            {
              key: 'stress',
              label: 'Индекс стресса',
              values: [getSafeNumber(resilience?.stress_index?.score)],
              color: '#2AC8E0',
            },
            {
              key: 'monotony',
              label: 'Индекс монотонии',
              values: [getSafeNumber(resilience?.monotony_index?.score)],
              color: '#F323C6',
            },
            {
              key: 'motivation',
              label: 'Снижение мотивации',
              values: [getSafeNumber(resilience?.decreased_motivation?.score)],
              color: '#AE3FF3',
            },
          ],
        },
      }
    }

    const buildFatigueFromBackend = (data) => {
      const backendData = normalizeBackendData(data)
      const nextFatigue = backendData?.chronic_fatigue_index || {}

      if (Array.isArray(nextFatigue?.categories)) {
        return {
          passedCount: nextFatigue.passed_count,
          currentValue: nextFatigue.current_value,
          levels: mapFatigueCategories(nextFatigue.categories),
          positions: Array.isArray(nextFatigue?.by_position)
              ? nextFatigue.by_position.map((item) => ({
                name: item.job_level,
                value: getSafeNumber(item.value),
                max: 10,
                warning: item?.warning_text,
              }))
              : [],
        }
      }

      const fatigue = data?.chronic_fatigue_index || {}

      const levels = Array.isArray(fatigue?.value)
          ? fatigue.value.map((item, index) => ({
            key: `level_${index}`,
            label: item?.title || '',
            change: getSignedChange(item?.score, item?.previous, item?.trend),
            value: getSafeNumber(item?.score),
            color: getFatigueLevelColor(item?.title),
          }))
          : []

      const positions = Array.isArray(fatigue?.by_position)
          ? fatigue.by_position.map((item) => ({
            name: item?.title || '',
            value: getSafeNumber(item?.score),
            max: 10,
            color: getFatigueLevelColor(item?.title),
            warning: item?.warning_text,
          }))
          : []

      return {
        passedCount: fatigue?.passed_count,
        currentValue: fatigue.current_value,
        levels,
        positions,
      }
    }

    const fillCardsData = (backendData) => {
      if (!hasSubscriptionAccess.value) {
        stabilityItems.value = DEFAULT_STABILITY_ITEMS.map((item) => ({ ...item }))
        stabilityChart.value = {
          labels: [...DEFAULT_STABILITY_CHART.labels],
          datasets: DEFAULT_STABILITY_CHART.datasets.map((item) => ({
            ...item,
            values: [...item.values],
          })),
        }
        fatigueLevels.value = DEFAULT_FATIGUE_LEVELS.map((item) => ({ ...item }))
        fatiguePositions.value = DEFAULT_FATIGUE_POSITIONS.map((item) => ({ ...item }))
        stabilitySubindexLevelCounts.value = {}
        stabilityPassedCount.value = null
        fatiguePassedCount.value = null
        fatigueCurrentValue.value = null
        return
      }

      const mappedStability = buildStabilityFromBackend(backendData)
      const mappedFatigue = buildFatigueFromBackend(backendData)

      stabilityItems.value = mappedStability.items
      stabilityChart.value = mappedStability.chart
      fatigueLevels.value = mappedFatigue.levels
      fatiguePositions.value = mappedFatigue.positions
      stabilitySubindexLevelCounts.value = mappedStability.subindexLevelCounts
      stabilityPassedCount.value = mappedStability.passedCount
      fatiguePassedCount.value = mappedFatigue.passedCount
      fatigueCurrentValue.value = mappedFatigue.currentValue
    }

    const fetchData = () => {
      // if (!hasSubscriptionAccess.value) {
      //   fillCardsData(null)
      //   return
      // }

      dataIsLoading.value = true

      store.dispatch('analytics/getEmotionalStabilityDynamics', {
        group_id: selectedGroup.value === 'all' ? null : selectedGroup.value,
        date: periodInterval.value,
      }).then(() => {
        fillCardsData(emotionalStabilityDynamicsData.value)
      }).finally(() => {
        dataIsLoading.value = false
      })
    }

    watch(
        () => [emotionalStabilityDynamicsData.value, hasSubscriptionAccess.value],
        () => {
          if (hasSavedCustomDate) {
            return
          }

          fillCardsData(emotionalStabilityDynamicsData.value)
        },
        { immediate: true }
    )

    watch(
        hasSubscriptionAccess,
        (hasAccess, hadAccess) => {
          if (hasAccess && !hadAccess) {
            fetchData()
          }
        }
    )

    watch(
        () => [selectedGroup.value, periodInterval.value],
        () => {
          fetchData()
        },
        { immediate: hasSavedCustomDate }
    )

    watch(
        () => selectedPeriod.value,
        () => {
          if (Array.isArray(selectedPeriod.value) && selectedPeriod.value.length === 2 && selectedPeriod.value[0] && selectedPeriod.value[1]) {
            setMonitoringDateSetting(dateStorageKey, selectedPeriod.value)
          }
        }
    )

    return {
      selectedGroup,
      selectedPeriod,
      groupOptions,
      dataIsLoading,
      stabilityItems,
      stabilityChart,
      fatigueLevels,
      fatiguePositions,
      stabilitySubindexLevelCounts,
      stabilityPassedCount,
      fatiguePassedCount,
      fatigueCurrentValue,
      fatigueHeaderLines,
      formatPassedCount,
    }
  },
}
</script>

<style scoped lang="scss">
.emotional-stability {
  width: 100%;
  text-align: start;
}

.emotional-stability__header {
  margin-bottom: 20px;
}

.emotional-stability__title {
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  @media (max-width: $mobile-breakpoint) {
    padding: 0 20px;
  }
}

.emotional-stability__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.emotional-stability__select {
  width: 350px;
}

.emotional-stability__date {
  width: 200px;
}

.emotional-stability__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

.emotional-stability__grid :deep(.ant-card-body) {
  padding: 0;
}

.emotional-stability__grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

@media (max-width: $tablet-breakpoint) {
  .emotional-stability__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .emotional-stability__filters {
    padding: 0 20px;
  }

  .emotional-stability__select,
  .emotional-stability__date {
    width: 100%;
  }
}

.compact-range-picker {
  display: flex;
  align-items: center;
}

:deep(.compact-range-picker .ant-picker-input) {
  flex: 0 1 auto;
}

:deep(.compact-range-picker .ant-picker-input > input) {
  padding: 0;
  font-size: 14px;
  text-align: center;
}

:deep(.compact-range-picker .ant-picker-range-separator) {
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
}

:deep(.compact-range-picker .ant-picker-active-bar) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-clear) {
  display: none;
}

:deep(.compact-range-picker .ant-picker-suffix) {
  margin-left: 6px;
  color: rgba(0, 0, 0, 0.45);
}

:deep(.compact-range-picker .ant-picker-input-placeholder > input) {
  text-align: center;
}

.locked {
  opacity: 0.5;
}

.subsription-locked {
  margin-bottom: 16px;
}
</style>
